<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import TravelataMarkLogo from '@/shared/ui/logos/TravelataMarkLogo.vue';
  import { HeaderLogoSize, LogoType, TravelataMark } from '../model/types';

  const PARTNERSHIP_LABEL = 'в партнёрстве';
  const PARTNERSHIP_PREPOSITION = 'с';

  export interface HeaderLogoProps {
    logoType?: LogoType;
    partnerSrc?: string;
    partnerHref?: string;
    travelataMark?: TravelataMark;
    size?: `${HeaderLogoSize}`;
  }

  const props = withDefaults(defineProps<HeaderLogoProps>(), {
    logoType: LogoType.PartnerOnly,
    partnerSrc: '',
    partnerHref: '',
    travelataMark: TravelataMark.Color,
    size: HeaderLogoSize.M,
  });

  // Знак Travelata в композите стоит в потоке, а в режиме logoType=1 центрируется по шапке.
  const isComposite = computed(() => props.logoType === LogoType.Composite);
  const isCentered = computed(() => props.logoType === LogoType.PartnerAndTravelata);
  const captionVariant = computed(() =>
    props.size === HeaderLogoSize.L ? TypoVariant.BodyMBoldShort : TypoVariant.CaptionSBoldShort,
  );
</script>

<template>
  <div
    :class="['header-logo', `header-logo_size-${size}`]"
    data-testid="site-header-logo"
  >
    <a
      :href="partnerHref || undefined"
      class="header-logo__partner"
      data-testid="site-header-logo-partner"
    >
      <img
        v-if="partnerSrc"
        :src="partnerSrc"
        class="header-logo__partner-image"
        alt=""
      />
    </a>

    <template v-if="isComposite">
      <span
        class="header-logo__divider"
        aria-hidden="true"
      />
      <span class="header-logo__partnership">
        <Typo
          :variant="captionVariant"
          tag="span"
        >
          {{ PARTNERSHIP_LABEL }}
        </Typo>
        <span class="header-logo__partnership-row">
          <Typo
            :variant="captionVariant"
            tag="span"
          >
            {{ PARTNERSHIP_PREPOSITION }}
          </Typo>
          <TravelataMarkLogo
            :variant="travelataMark"
            class="header-logo__mark header-logo__mark_inline"
            data-testid="site-header-logo-mark"
          />
        </span>
      </span>
    </template>

    <TravelataMarkLogo
      v-if="isCentered"
      :variant="travelataMark"
      class="header-logo__mark header-logo__mark_centered"
      data-testid="site-header-logo-mark"
    />
  </div>
</template>

<style scoped lang="scss">
  .header-logo {
    $root: &;

    align-items: center;
    display: flex;
    gap: var(--spacing-xs);

    &__partner {
      align-items: center;
      display: inline-flex;
      flex: 0 0 auto;
      height: 24px;

      #{$root}_size-l & {
        height: 32px;
      }
    }

    &__partner-image {
      height: 100%;
      width: auto;
    }

    &__divider {
      align-self: stretch;
      background: var(--color-text-on-brand);
      flex: 0 0 auto;
      transform: skewX(-18deg);
      width: 1px;
    }

    &__partnership {
      color: var(--color-text-on-brand);
      display: flex;
      flex-direction: column;
      gap: 2px;
      white-space: nowrap;
    }

    &__partnership-row {
      align-items: flex-end;
      display: flex;
      gap: 4px;
    }

    &__mark {
      flex: 0 0 auto;
      height: 20px;
      width: 111px;

      &_inline {
        height: 11px;
        width: 51px;

        #{$root}_size-l & {
          height: 15px;
          width: 68px;
        }
      }

      &_centered {
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);

        #{$root}_size-l & {
          height: 32px;
          width: 148px;
        }
      }
    }
  }
</style>
